import React, { useEffect, useMemo, useRef, useState } from "react";
import { trackMarketingEvent } from "./MarketingObservability.tsx";

const VENDOR_EVENT_NAMES = {
  campaign_landing_view: "landing_view",
  campaign_engaged_session: "engaged_landing",
  campaign_primary_cta_click: "primary_cta_click",
  mcp_auth_start: "auth_start",
  campaign_proof_view: "proof_view"
};

const API_BASE = "/api/landing";
const AUTH_URL = "https://mcp.obto.co/ms/v2/auth.bto";
const CAMPAIGN = "prompt-to-autopilot-foundation";

const uid = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID().replace(/-/g, "_");
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
};

const getDevice = () => window.innerWidth < 720 ? "mobile" : window.innerWidth < 1100 ? "tablet" : "desktop";

const safeReferrerDomain = () => {
  try { return document.referrer ? new URL(document.referrer).hostname.slice(0, 120) : ""; }
  catch { return ""; }
};

// Click-id fallback: a link that arrives with fbclid / gclid / li_fat_id but no utm_source still
// came from a known channel. Only the PRESENCE of the id is used - its value never leaves the browser.
const CLICK_ID_CHANNELS = [
  ["fbclid", "facebook", "referral"],
  ["gclid", "google", "cpc"],
  ["li_fat_id", "linkedin", "paid_social"]
];

const readClickIds = (params) => {
  const out = {};
  CLICK_ID_CHANNELS.forEach(([key]) => { if (params.get(key)) out[key] = true; });
  return out;
};

const readUtm = () => {
  const params = new URLSearchParams(window.location.search);
  const utm = {
    source: (params.get("utm_source") || "").slice(0, 80),
    medium: (params.get("utm_medium") || "").slice(0, 80),
    campaign: (params.get("utm_campaign") || "").slice(0, 100),
    content: (params.get("utm_content") || "").slice(0, 100),
    term: (params.get("utm_term") || "").slice(0, 100)
  };
  if (!utm.source) {
    const hit = CLICK_ID_CHANNELS.find(([key]) => params.get(key));
    if (hit) { utm.source = hit[1]; if (!utm.medium) utm.medium = hit[2]; }
  }
  return utm;
};

const buildAuthUrl = (activationId) => {
  const target = new URL(AUTH_URL);
  const completion = new URL("https://staging.obto.co/ms/attribution-complete.bto");
  const utm = readUtm();
  [["utm_source", utm.source], ["utm_medium", utm.medium], ["utm_campaign", utm.campaign], ["utm_content", utm.content], ["utm_term", utm.term]].forEach(([key, value]) => {
    if (value) {
      target.searchParams.set(key, value.slice(0, 100));
      completion.searchParams.set(key, value.slice(0, 100));
    }
  });
  target.searchParams.set("source_page", "from-prompt-to-autopilot");
  completion.searchParams.set("source_page", "from-prompt-to-autopilot");
  if (activationId) {
    target.searchParams.set("activation_id", activationId);
    completion.searchParams.set("activation_id", activationId);
    target.searchParams.set("redirect", completion.toString());
  }
  return target.toString();
};

const sessionId = (key) => {
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const value = uid();
    window.sessionStorage.setItem(key, value);
    return value;
  } catch {
    return uid();
  }
};

function useCampaignAnalytics() {
  const context = useRef(null);
  if (!context.current && typeof window !== "undefined") {
    const privacyOptOut = navigator.doNotTrack === "1" || navigator.globalPrivacyControl === true;
    context.current = {
      enabled: !privacyOptOut,
      activation_id: sessionId("obto_activation_id"),
      anonymous_id: sessionId("obto_anonymous_id"),
      session_id: sessionId("obto_session_id"),
      utm: readUtm(),
      click_ids: readClickIds(new URLSearchParams(window.location.search))
    };
  }

  const track = async (event_name, properties = {}) => {
    const ctx = context.current;
    if (!ctx?.enabled) return { skipped: true };
    const vendorEvent = VENDOR_EVENT_NAMES[event_name];
    if (vendorEvent) {
      trackMarketingEvent(vendorEvent, { source_page: "from-prompt-to-autopilot", ...properties });
    }
    try {
      const response = await fetch(`${API_BASE}/campaign-events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          event_id: uid(),
          activation_id: ctx.activation_id,
          event_name,
          anonymous_id: ctx.anonymous_id,
          session_id: ctx.session_id,
          occurred_at: new Date().toISOString(),
          landing_path: (window.location.pathname + window.location.hash).slice(0, 260),
          source_page: "from-prompt-to-autopilot",
          referrer_domain: safeReferrerDomain(),
          device_class: getDevice(),
          consent_state: "essential_attribution",
          utm: ctx.utm,
          click_ids: ctx.click_ids,
          ...properties
        })
      });
      const type = response.headers.get("content-type") || "";
      if (!response.ok || !type.includes("application/json")) throw new Error(`event_http_${response.status}`);
      return await response.json();
    } catch (error) {
      console.warn("Campaign event was not recorded", error);
      return { ok: false };
    }
  };

  return {
    activationId: context.current?.activation_id || "",
    enabled: Boolean(context.current?.enabled),
    track
  };
}

const proofs = [
  {
    name: "SupplyPulse",
    title: "Catch supply risk. Route the intervention. Preserve the decision.",
    verified: "Verified application, API, persisted reroute, audit timeline, and responsive UI.",
    extension: "Proposed extension: scheduled risk monitoring opens a bounded approval when policy is crossed.",
    href: "https://supplypulse-demo-26-staging.obto.co/"
  },
  {
    name: "VenueFlow",
    title: "Detect overcapacity. Apply reassignment policy. Record the outcome.",
    verified: "Verified live state, policy-aware response, persisted decision, and public API behavior.",
    extension: "Proposed extension: an event monitor proposes a compliant reassignment and pauses for guest-impact approval.",
    href: "https://venueflow-staging.obto.co/"
  },
  {
    name: "DealDesk",
    title: "Evaluate discount policy. Route approval. Preserve quote state.",
    verified: "Verified quote economics, manager and finance routing, persistence, proposal, and audit history.",
    extension: "Proposed extension: quote changes trigger bounded approval routing, SLA reminders, and escalation.",
    href: "https://dealdesk-staging.obto.co/"
  }
];

const faq = [
  ["Is OBTO another chatbot?", "No. OBTO is the application and operations platform exposed to capable AI clients through MCP."],
  ["Does autopilot remove human approval?", "No. Each workflow defines what may run automatically, what requires a person, and what must stop."],
  ["Is the result a mockup?", "Campaign proof requires deployment, live API behavior, persistence, validation, and a verified user action."],
  ["Can I keep using the AI interface I know?", "Use a capable client with verified MCP support. Compatibility is confirmed client by client rather than assumed."],
  ["What data does this campaign collect?", "OBTO records pseudonymous campaign source and funnel events in its first-party CRM so we can measure what works. Landing events contain no email, prompt text, tool arguments, endpoint tokens, or tenant data. Optional Google Analytics and LinkedIn measurement loads only after your Privacy choices allow it; enhanced matching and form-value sharing are off. Do Not Track and Global Privacy Control block measurement."],
];

export default function AutopilotCampaign() {
  const { activationId, enabled, track } = useCampaignAnalytics();
  const [openFaq, setOpenFaq] = useState(-1);
  const [engaged, setEngaged] = useState(false);
  const authUrl = useMemo(
    () => buildAuthUrl(activationId),
    [activationId]
  );

  useEffect(() => {
    document.title = "From Prompt to Autopilot | OBTO";
    track("campaign_landing_view");
    const started = Date.now();
    const timer = window.setTimeout(() => {
      setEngaged(true);
      track("campaign_engaged_session", { engagement_reason: "active_30_seconds", active_seconds: 30 });
    }, 30000);
    return () => window.clearTimeout(timer);
  }, []);

  const primaryClick = (location) => {
    track("campaign_primary_cta_click", {
      cta_location: location,
      destination: AUTH_URL
    });
    track("mcp_auth_start", {
      cta_location: location,
      destination: AUTH_URL
    });
  };

  return (
    <main className="ap-page">
      <style>{`
        :root{color-scheme:dark}*{box-sizing:border-box}.ap-page{--ink:#121214;--panel:#1E1E22;--line:#2E2E34;--mint:#B4AAFF;--gold:#B4AAFF;--text:#F5F3EF;--muted:#9A98A3;background:var(--ink);color:var(--text);font-family:"Figtree",system-ui,sans-serif;line-height:1.55;min-height:100vh}.ap-page a{color:inherit}.ap-wrap{width:min(1160px,calc(100% - 40px));margin:auto}.ap-nav{height:72px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid var(--line)}.ap-logo{font-weight:800;letter-spacing:.14em}.ap-nav a{text-decoration:none}.ap-button{display:inline-flex;align-items:center;justify-content:center;padding:14px 22px;border-radius:999px;background:#5345C9;border:1px solid #5345C9;color:#FFFFFF!important;font-weight:750;text-decoration:none;border:1px solid var(--mint);transition:.2s}.ap-button:hover{transform:translateY(-2px);filter:brightness(1.05)}.ap-button.secondary{background:transparent;color:var(--text)!important;border-color:var(--line)}.ap-hero{padding:88px 0 72px;display:grid;grid-template-columns:1.1fr .9fr;gap:56px;align-items:center}.ap-eyebrow,.ap-kicker{color:var(--mint);font-size:12px;letter-spacing:.18em;font-weight:800}.ap-hero h1{font-size:clamp(44px,6vw,78px);line-height:1.02;letter-spacing:-.045em;margin:18px 0 24px}.ap-lede{font-size:clamp(18px,2vw,22px);color:var(--muted);max-width:720px}.ap-actions{display:flex;gap:12px;flex-wrap:wrap;margin:32px 0}.ap-trust{font-size:13px;color:var(--gold);letter-spacing:.06em}.ap-flow{background:#1E1E22;border:1px solid var(--line);border-radius:28px;padding:24px;box-shadow:0 30px 90px rgba(0,0,0,.35)}.ap-flow-step{padding:17px;border:1px solid var(--line);border-radius:14px;background:rgba(255,255,255,.025)}.ap-arrow{text-align:center;color:var(--mint);padding:6px}.ap-section{padding:86px 0;border-top:1px solid rgba(46,46,52,.7)}.ap-section h2{font-size:clamp(34px,4vw,54px);line-height:1.08;letter-spacing:-.035em;margin:14px 0 20px}.ap-sub{color:var(--muted);font-size:18px;max-width:760px}.ap-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:38px}.ap-card{background:var(--panel);border:1px solid var(--line);border-radius:20px;padding:25px}.ap-card h3{font-size:22px;line-height:1.25;margin:8px 0 14px}.ap-label{font-size:11px;color:var(--gold);font-weight:800;letter-spacing:.12em}.ap-verified{color:#F5F3EF}.ap-extension{color:var(--muted);font-size:14px;border-top:1px solid var(--line);padding-top:14px;margin-top:14px}.ap-card-link{display:inline-block;margin-top:18px;color:var(--mint)!important;font-weight:700}.ap-cycle{display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin-top:34px}.ap-cycle div{background:var(--panel);border:1px solid var(--line);border-radius:14px;padding:20px 12px;text-align:center;font-weight:700}.ap-steps{counter-reset:step;display:grid;gap:12px;margin-top:34px}.ap-step{counter-increment:step;display:grid;grid-template-columns:52px 1fr;align-items:center;background:var(--panel);border:1px solid var(--line);border-radius:16px;padding:17px}.ap-step:before{content:counter(step,decimal-leading-zero);color:var(--mint);font-weight:800}.ap-faq{margin-top:34px}.ap-faq button{width:100%;display:flex;justify-content:space-between;gap:20px;text-align:left;background:transparent;color:var(--text);border:0;border-top:1px solid var(--line);padding:22px 0;font:inherit;font-weight:750;cursor:pointer}.ap-answer{color:var(--muted);padding:0 0 22px;max-width:800px}.ap-final{text-align:center;background:none;padding:100px 20px}.ap-final h2{max-width:800px;margin:16px auto 22px}.ap-note{font-size:12px;color:var(--muted);margin-top:18px}.ap-test{position:fixed;right:14px;bottom:14px;background:#26262B;color:var(--mint);border:1px solid var(--line);border-radius:999px;padding:8px 12px;font-size:11px;z-index:20}@media(max-width:900px){.ap-hero{grid-template-columns:1fr;padding-top:58px}.ap-grid{grid-template-columns:1fr}.ap-cycle{grid-template-columns:repeat(2,1fr)}}@media(max-width:560px){.ap-wrap{width:min(100% - 24px,1160px)}.ap-nav .ap-button{padding:10px 14px}.ap-hero h1{font-size:44px}.ap-actions{display:grid}.ap-button{width:100%}.ap-section{padding:64px 0}.ap-cycle{grid-template-columns:1fr 1fr}.ap-card{padding:20px}}
      `}</style>

      <div className="ap-wrap">
        <nav className="ap-nav" aria-label="Campaign navigation">
          <a className="ap-logo" href="/" aria-label="OBTO home"><img src="https://www.obto.co/ms/filereader.bto?n=mush6zo8f1132076_www_brand_obto-lockup-reverse.svg" alt="OBTO" width={112} height={26} style={{ display: "block", height: "26px", width: "auto" }} /></a>
          <a className="ap-button" href={authUrl} onClick={() => primaryClick("nav")}>Get your MCP endpoint</a>
        </nav>

        <section className="ap-hero">
          <div>
            <div className="ap-eyebrow">FROM PROMPT TO AUTOPILOT</div>
            <h1>Build the workflow through chat. Keep the business process running on OBTO.</h1>
            <p className="ap-lede">Connect your frontier AI to OBTO's governed MCP endpoint. Ask it to create the application, APIs, data, business rules, and operating workflow. Then verify the result—and leave OBTO monitoring, triggering, and recording the work within the controls you choose.</p>
            <div className="ap-actions">
              <a className="ap-button" href={authUrl} onClick={() => primaryClick("hero")}>Get your OBTO MCP endpoint</a>
              <a className="ap-button secondary" href="#proof" onClick={() => track("campaign_proof_view", { proof_name: "gallery", proof_type: "navigation" })}>Watch a verified workflow</a>
            </div>
            <div className="ap-trust">Human-led. Policy-bounded. Auditable. Built through MCP.</div>
          </div>
          <div className="ap-flow" aria-label="From prompt to operating workflow">
            {["Chat request", "OBTO MCP tools", "Deployed app + API + data", "Monitored workflow", "Approval + audit proof"].map((item, index) => <React.Fragment key={item}><div className="ap-flow-step">{item}</div>{index < 4 && <div className="ap-arrow">↓</div>}</React.Fragment>)}
          </div>
        </section>
      </div>

      <section className="ap-section" id="proof">
        <div className="ap-wrap">
          <div className="ap-kicker">WORKFLOWS BUILT AND VERIFIED ON OBTO</div>
          <h2>The application is the control surface. The workflow is the proof.</h2>
          <p className="ap-sub">Each proof separates what has been implemented and verified from what remains a proposed autopilot extension.</p>
          <div className="ap-grid">
            {proofs.map(proof => <article className="ap-card" key={proof.name}>
              <div className="ap-label">{proof.name.toUpperCase()}</div>
              <h3>{proof.title}</h3>
              <p className="ap-verified">{proof.verified}</p>
              <p className="ap-extension">{proof.extension}</p>
              <a className="ap-card-link" href={proof.href} target="_blank" rel="noopener noreferrer" onClick={() => track("campaign_proof_view", { proof_name: proof.name, proof_type: "live_application" })}>Inspect the live proof →</a>
            </article>)}
          </div>
        </div>
      </section>

      <section className="ap-section">
        <div className="ap-wrap">
          <div className="ap-kicker">A CONTROL SURFACE FOR AUTOPILOT</div>
          <h2>Autopilot does not mean unaccountable.</h2>
          <p className="ap-sub">Choose what can run automatically, what must request approval, what should trigger an alert, and what evidence must remain. OBTO stays in the loop after deployment so the workflow can be monitored, operated, and improved.</p>
          <div className="ap-cycle">{["Observe", "Evaluate rule", "Act or approve", "Verify", "Record", "Escalate failure"].map(x => <div key={x}>{x}</div>)}</div>
        </div>
      </section>

      <section className="ap-section">
        <div className="ap-wrap">
          <div className="ap-kicker">THE FIRST SUCCESS</div>
          <h2>Start with one workflow worth verifying.</h2>
          <div className="ap-steps">
            {["Get your tenant-bound OBTO MCP endpoint.", "Connect it to a supported frontier AI client.", "Ask for one thin, measurable workflow.", "Let OBTO scaffold the application and operating logic.", "Validate the app, call the routes, and verify one user action.", "Add the trigger, approval, monitoring, and audit proof that make the workflow operational."].map(step => <div className="ap-step" key={step}>{step}</div>)}
          </div>
        </div>
      </section>

      <section className="ap-section">
        <div className="ap-wrap">
          <div className="ap-kicker">WHO THIS IS FOR</div>
          <div className="ap-grid">
            <div className="ap-card"><div className="ap-label">FOUNDERS</div><h3>Clear the internal-tool backlog without assembling another app stack.</h3></div>
            <div className="ap-card"><div className="ap-label">OPERATORS</div><h3>Turn a repeated exception into a bounded, measurable workflow.</h3></div>
            <div className="ap-card"><div className="ap-label">ARCHITECTS + AGENCIES</div><h3>Deliver through an open protocol while keeping deployment and operations governed.</h3></div>
          </div>
        </div>
      </section>

      <section className="ap-section">
        <div className="ap-wrap">
          <div className="ap-kicker">QUESTIONS</div>
          <h2>Make the trust boundary explicit.</h2>
          <div className="ap-faq">{faq.map(([question, answer], index) => <div key={question}>
            <button onClick={() => setOpenFaq(openFaq === index ? -1 : index)} aria-expanded={openFaq === index}><span>{question}</span><span>{openFaq === index ? "−" : "+"}</span></button>
            {openFaq === index && <div className="ap-answer">{answer}</div>}
          </div>)}</div>
        </div>
      </section>

      <section className="ap-final">
        <div className="ap-kicker">FROM PROMPT TO AUTOPILOT</div>
        <h2>Build it through chat. Operate it on OBTO.</h2>
        <a className="ap-button" href={authUrl} onClick={() => primaryClick("final")}>Get your OBTO MCP endpoint</a>
        <div className="ap-note">{enabled
          ? "First-party campaign attribution is active. Optional Google Analytics and LinkedIn measurement follows your Privacy choices."
          : "Your browser sends Do Not Track or Global Privacy Control, so first-party and external measurement are blocked."}</div>
      </section>
    </main>
  );
}

